import {int, panic, Procedure, UUID} from "@opendaw/lib-std"
import {BpmDetector, SoundfontMetaData} from "@opendaw/studio-adapters"
import {
    AudioWorklets,
    EngineWorklet,
    GlobalSampleLoaderManager,
    GlobalSoundfontLoaderManager,
    Project,
    SampleService,
    SoundfontProvider,
    SoundfontService,
    Workers
} from "@opendaw/studio-core"
import {WasmEngine} from "@opendaw/studio-core-wasm"
import {OpenSampleProvider} from "@/samples/OpenSampleProvider"
import {AcidKit} from "@/samples/AcidKit"
import {AcidTrack, AcidTrackSpec} from "@/generate/AcidTrack"
import {DrumMachine} from "@/project/DrumMachine"
import {BassLine} from "@/project/BassLine"
import {Arrangement, Arranger} from "@/project/Arranger"

export type BootUrls = {
    workersUrl: string
    workletsUrl: string
    wasmProcessorUrl: string
    wasmOfflineWorkerUrl: string
}

// Everything that lives for the page: one context, one engine, one sample cache shared by every track.
export type Env = {
    audioContext: AudioContext
    audioWorklets: AudioWorklets
    sampleManager: GlobalSampleLoaderManager
    soundfontManager: GlobalSoundfontLoaderManager
}

// `arranger.start()` launches the first clip, which also starts the transport.
export type Track = {
    project: Project
    worklet: EngineWorklet
    spec: AcidTrackSpec
    arranger: Arranger
    drums: DrumMachine
    bass: BassLine
}

const NoSoundfonts: SoundfontProvider = {
    fetch: async (uuid: UUID.Bytes): Promise<[ArrayBuffer, SoundfontMetaData]> =>
        panic(`Acid has no soundfonts (${UUID.toString(uuid)})`)
}

export const setup = async ({workersUrl, workletsUrl, wasmProcessorUrl, wasmOfflineWorkerUrl}: BootUrls,
                            progress: Procedure<string>): Promise<Env> => {
    progress("Installing workers")
    await Workers.install(workersUrl)
    AudioWorklets.install(workletsUrl)
    const audioContext = new AudioContext({sampleRate: 48000, latencyHint: 0})
    const audioWorklets = await AudioWorklets.createFor(audioContext)
    progress("Loading engine")
    WasmEngine.install({
        processorUrl: wasmProcessorUrl,
        offlineWorkerUrl: wasmOfflineWorkerUrl,
        wasmUrl: `${import.meta.env.BASE_URL}wasm-engine`
    })
    if (!await WasmEngine.ensureReady(audioContext)) {
        return panic("Could not load the audio engine")
    }
    const sampleManager = new GlobalSampleLoaderManager(OpenSampleProvider)
    const soundfontManager = new GlobalSoundfontLoaderManager(NoSoundfonts)
    const total = AcidKit.Pads.length
    const loaded = new Set<string>()
    progress(`Loading samples 0/${total}`)
    await Promise.all(AcidKit.Pads.map(({sample}) =>
        sampleManager.getAudioData(UUID.parse(sample.uuid)).then(() => {
            loaded.add(sample.uuid)
            progress(`Loading samples ${loaded.size}/${total}`)
        })))
    return {audioContext, audioWorklets, sampleManager, soundfontManager}
}

// One composed track in its own project and worklet. The caller owns it and terminates the project to stop it.
export const compose = async ({audioContext, audioWorklets, sampleManager, soundfontManager}: Env,
                              seed: int, progress: Procedure<string>): Promise<Track> => {
    const spec = AcidTrack.compose(seed)
    console.debug(`acid seed ${spec.seed}, ${spec.bpm} bpm`)
    progress("Composing track")
    const project = Project.new({
        audioContext,
        audioWorklets,
        sampleManager,
        soundfontManager,
        sampleService: new SampleService(audioContext, BpmDetector.Unknown),
        soundfontService: new SoundfontService()
    })
    const drums = DrumMachine.create(project, spec)
    const bass = BassLine.create(project, spec)
    const arranger = project.own(new Arranger(project, drums, bass, Arrangement.plan(seed)))
    progress("Starting engine")
    const worklet = project.startAudioWorklet()
    await project.engine.isReady()
    return {project, worklet, spec, arranger, drums, bass}
}
